import { cellCount, createField, type GridSpec, type ScalarField } from './grid.ts';

/**
 * Layered snow columns, one per snowpack-grid cell, stored structure-of-arrays so everything
 * stays in typed arrays (worker-transferable, GPU-friendly later).
 *
 * Layer k of cell i lives at index i·maxLayers + k; k = 0 is the bottom layer. Layers carry mass
 * (kg/m², i.e. mm water equivalent) and density (kg/m³); thickness = mass / density. `mass` is
 * the frozen part; liquid water held in a layer is stored separately in `water` (kg/m²) and does
 * not change its thickness.
 */

/**
 * Grain classes, after Fierz et al. (2009), International Classification for Seasonal Snow on
 * the Ground. Numeric codes are stored in a Uint8Array.
 */
export const Grain = {
  /** Precipitation particles — new snow. */
  PP: 0,
  /** Decomposing / fragmented particles. */
  DF: 1,
  /** Rounded grains — settled old snow. */
  RG: 2,
  /** Wind-packed rounded grains — wind slab. */
  RGwp: 3,
  /** Faceted crystals. */
  FC: 4,
  /** Depth hoar. */
  DH: 5,
  /** Surface hoar. */
  SH: 6,
  /** Graupel. */
  PPgp: 7,
  /** Melt forms. */
  MF: 8,
  /** Ice formation / crust. */
  IF: 9,
  /** Avalanche debris (not a Fierz class): dense, mixed, deposited by a flow. */
  Debris: 10,
} as const;
export type Grain = (typeof Grain)[keyof typeof Grain];

export const GRAIN_LABELS: Record<Grain, string> = {
  [Grain.PP]: 'New snow',
  [Grain.DF]: 'Decomposing',
  [Grain.RG]: 'Rounded (old snow)',
  [Grain.RGwp]: 'Wind slab',
  [Grain.FC]: 'Facets',
  [Grain.DH]: 'Depth hoar',
  [Grain.SH]: 'Surface hoar',
  [Grain.PPgp]: 'Graupel',
  [Grain.MF]: 'Melt forms',
  [Grain.IF]: 'Ice / crust',
  [Grain.Debris]: 'Avalanche debris',
};

export const DEFAULT_MAX_LAYERS = 16;

/** Adjacent layers of the same grain deposited within this many hours are merged. */
export const MERGE_WINDOW_HOURS = 12;

/**
 * A top layer thinner than this (m) absorbs a new deposit from the same merge window even if the
 * grain differs — sub-2 cm layers are below what matters for stability, and without this hourly
 * alternation of new snow and wind slab during a storm would produce one layer per hour.
 */
export const MIN_LAYER_THICKNESS = 0.02;

/** Mass below which a layer is considered empty (kg/m²). */
const EMPTY_MASS = 1e-4;

export interface SnowColumns {
  spec: GridSpec;
  maxLayers: number;
  /** Number of layers in each cell. */
  count: Uint8Array;
  mass: Float32Array;
  density: Float32Array;
  grain: Uint8Array;
  /** Simulation hour the layer was deposited (negative = before the scenario). */
  depositedAt: Float32Array;
  /** Liquid water held in the layer (kg/m²). */
  water: Float32Array;
}

export function createColumns(spec: GridSpec, maxLayers = DEFAULT_MAX_LAYERS): SnowColumns {
  const n = cellCount(spec);
  return {
    spec,
    maxLayers,
    count: new Uint8Array(n),
    mass: new Float32Array(n * maxLayers),
    density: new Float32Array(n * maxLayers),
    grain: new Uint8Array(n * maxLayers),
    depositedAt: new Float32Array(n * maxLayers),
    water: new Float32Array(n * maxLayers),
  };
}

/**
 * Put a layer on top of a column. Merges into the top layer when it was deposited within
 * MERGE_WINDOW_HOURS and either has the same grain or one of the two is thinner than
 * MIN_LAYER_THICKNESS (the heavier part's grain wins). A merged top then cascades into the layer
 * below on the same rule. When the column is full, the two lowest layers are merged to make
 * room — deep layers matter least for the processes modelled.
 */
export function addLayer(
  cols: SnowColumns,
  cell: number,
  mass: number,
  density: number,
  grain: Grain,
  depositedAt: number,
): void {
  if (!(mass > EMPTY_MASS)) return;
  const base = cell * cols.maxLayers;
  const n = cols.count[cell]!;
  if (n > 0) {
    const top = base + n - 1;
    if (
      canMerge(
        cols.grain[top]! as Grain,
        cols.mass[top]! / cols.density[top]!,
        cols.depositedAt[top]!,
        grain,
        mass / density,
        depositedAt,
      )
    ) {
      mergeInto(cols, top, mass, density, grain, depositedAt, 0);
      // The merged top may now match the layer below (e.g. new snow absorbed into wind slab
      // sitting on the previous hour's wind slab).
      let k = top;
      while (k > base) {
        const below = k - 1;
        if (
          !canMerge(
            cols.grain[below]! as Grain,
            cols.mass[below]! / cols.density[below]!,
            cols.depositedAt[below]!,
            cols.grain[k]! as Grain,
            cols.mass[k]! / cols.density[k]!,
            cols.depositedAt[k]!,
          )
        )
          break;
        mergeInto(
          cols,
          below,
          cols.mass[k]!,
          cols.density[k]!,
          cols.grain[k]! as Grain,
          cols.depositedAt[k]!,
          cols.water[k]!,
        );
        cols.count[cell]!--;
        k = below;
      }
      return;
    }
  }
  if (n === cols.maxLayers) mergeBottomTwo(cols, cell);
  const k = base + cols.count[cell]!;
  cols.mass[k] = mass;
  cols.density[k] = density;
  cols.grain[k] = grain;
  cols.depositedAt[k] = depositedAt;
  cols.water[k] = 0;
  cols.count[cell]!++;
}

function canMerge(g0: Grain, h0: number, t0: number, g1: Grain, h1: number, t1: number): boolean {
  if (Math.abs(t0 - t1) > MERGE_WINDOW_HOURS) return false;
  if (g0 === g1) return true;
  // Weak layers are typically thin; absorbing them would erase exactly what matters.
  if (WEAK_LAYER_GRAINS.has(g0) || WEAK_LAYER_GRAINS.has(g1)) return false;
  return h0 < MIN_LAYER_THICKNESS || h1 < MIN_LAYER_THICKNESS;
}

/** Grains that form weak layers; never merged into a different grain. */
export const WEAK_LAYER_GRAINS: ReadonlySet<Grain> = new Set<Grain>([
  Grain.FC,
  Grain.DH,
  Grain.SH,
  Grain.PPgp,
]);

/** Merge a deposit into layer index `k`; mass-weighted, volume-conserving. */
function mergeInto(
  cols: SnowColumns,
  k: number,
  mass: number,
  density: number,
  grain: Grain,
  depositedAt: number,
  water: number,
): void {
  const m0 = cols.mass[k]!;
  const total = m0 + mass;
  cols.water[k] = cols.water[k]! + water;
  // By volume so thickness is conserved: ρ = Σm / Σ(m/ρ).
  cols.density[k] = total / (m0 / cols.density[k]! + mass / density);
  cols.depositedAt[k] = (cols.depositedAt[k]! * m0 + depositedAt * mass) / total;
  if (mass > m0) cols.grain[k] = grain;
  cols.mass[k] = total;
}

function mergeBottomTwo(cols: SnowColumns, cell: number): void {
  const base = cell * cols.maxLayers;
  const n = cols.count[cell]!;
  const m0 = cols.mass[base]!;
  const m1 = cols.mass[base + 1]!;
  const total = m0 + m1;
  cols.density[base] = total / (m0 / cols.density[base]! + m1 / cols.density[base + 1]!);
  cols.depositedAt[base] =
    (cols.depositedAt[base]! * m0 + cols.depositedAt[base + 1]! * m1) / total;
  // The merged layer keeps the grain of the heavier part.
  if (m1 > m0) cols.grain[base] = cols.grain[base + 1]!;
  cols.mass[base] = total;
  cols.water[base] = cols.water[base]! + cols.water[base + 1]!;
  for (let k = 1; k < n - 1; k++) copyLayer(cols, base + k + 1, base + k);
  cols.count[cell] = n - 1;
}

function copyLayer(cols: SnowColumns, from: number, to: number): void {
  cols.mass[to] = cols.mass[from]!;
  cols.density[to] = cols.density[from]!;
  cols.grain[to] = cols.grain[from]!;
  cols.depositedAt[to] = cols.depositedAt[from]!;
  cols.water[to] = cols.water[from]!;
}

/**
 * Insert a layer at position `index` (0 = bottom), shifting the layers above it up. No merging:
 * this is how weak layers are placed inside an existing snowpack. Returns false if the column is
 * full.
 */
export function insertLayer(
  cols: SnowColumns,
  cell: number,
  index: number,
  mass: number,
  density: number,
  grain: Grain,
  depositedAt: number,
): boolean {
  const n = cols.count[cell]!;
  if (n >= cols.maxLayers || !(mass > EMPTY_MASS)) return false;
  const base = cell * cols.maxLayers;
  const at = Math.max(0, Math.min(index, n));
  for (let k = n; k > at; k--) copyLayer(cols, base + k - 1, base + k);
  const k = base + at;
  cols.mass[k] = mass;
  cols.density[k] = density;
  cols.grain[k] = grain;
  cols.depositedAt[k] = depositedAt;
  cols.water[k] = 0;
  cols.count[cell] = n + 1;
  return true;
}

/**
 * Insert a layer whose top lies `depth` metres (vertical) below the snow surface, splitting the
 * layer at that depth in two. Returns false if the snow isn't deeper than `depth` or the column
 * would overflow.
 */
export function insertAtDepth(
  cols: SnowColumns,
  cell: number,
  depth: number,
  mass: number,
  density: number,
  grain: Grain,
  depositedAt: number,
): boolean {
  const n = cols.count[cell]!;
  const base = cell * cols.maxLayers;
  let above = 0;
  for (let k = n - 1; k >= 0; k--) {
    const h = cols.mass[base + k]! / cols.density[base + k]!;
    if (above + h > depth + 1e-9) {
      const hTop = depth - above;
      // Exactly at a layer boundary: no split needed.
      if (hTop < 1e-6) return insertLayer(cols, cell, k + 1, mass, density, grain, depositedAt);
      if (n + 2 > cols.maxLayers) return false;
      // Split layer k: the part above `depth` goes on top of the new layer, the rest below it.
      const rho = cols.density[base + k]!;
      const topFraction = hTop / h;
      const water = cols.water[base + k]!;
      const g = cols.grain[base + k]! as Grain;
      const t = cols.depositedAt[base + k]!;
      cols.mass[base + k] = cols.mass[base + k]! * (1 - topFraction);
      cols.water[base + k] = water * (1 - topFraction);
      insertLayer(cols, cell, k + 1, mass, density, grain, depositedAt);
      insertLayer(cols, cell, k + 2, hTop * rho, rho, g, t);
      cols.water[base + k + 2] = water * topFraction;
      return true;
    }
    above += h;
  }
  return false;
}

/**
 * Split layer `k` (0 = bottom) of a column in two, the upper part `topThickness` metres thick
 * (same grain, density and age; liquid water shared by thickness). Surface processes — melt,
 * refreeze, faceting — act on the top decimetres; without a split they would change a whole
 * metre-thick layer at once. Returns false if the column is full or the split is degenerate.
 */
export function splitLayer(
  cols: SnowColumns,
  cell: number,
  k: number,
  topThickness: number,
): boolean {
  const n = cols.count[cell]!;
  if (n >= cols.maxLayers || k < 0 || k >= n) return false;
  const base = cell * cols.maxLayers;
  const idx = base + k;
  const h = cols.mass[idx]! / cols.density[idx]!;
  if (!(topThickness > 1e-6 && topThickness < h - 1e-6)) return false;
  const topFraction = topThickness / h;
  const mass = cols.mass[idx]!;
  const water = cols.water[idx]!;
  cols.mass[idx] = mass * (1 - topFraction);
  cols.water[idx] = water * (1 - topFraction);
  insertLayer(
    cols,
    cell,
    k + 1,
    mass * topFraction,
    cols.density[idx]!,
    cols.grain[idx]! as Grain,
    cols.depositedAt[idx]!,
  );
  cols.water[idx + 1] = water * topFraction;
  return true;
}

export function layerThickness(cols: SnowColumns, layer: number): number {
  return cols.mass[layer]! / cols.density[layer]!;
}

/**
 * Remove up to `mass` kg/m² from the top of a column, only from the contiguous run of top layers
 * that `canErode` accepts. Returns the mass and thickness actually removed.
 */
export function erodeTop(
  cols: SnowColumns,
  cell: number,
  mass: number,
  canErode: (grain: Grain, depositedAt: number) => boolean,
): { mass: number; thickness: number } {
  const base = cell * cols.maxLayers;
  let remaining = mass;
  let thickness = 0;
  while (remaining > EMPTY_MASS && cols.count[cell]! > 0) {
    const top = base + cols.count[cell]! - 1;
    if (!canErode(cols.grain[top]! as Grain, cols.depositedAt[top]!)) break;
    const take = Math.min(remaining, cols.mass[top]!);
    thickness += take / cols.density[top]!;
    cols.water[top] = cols.water[top]! * (1 - take / cols.mass[top]!);
    cols.mass[top] = cols.mass[top]! - take;
    remaining -= take;
    if (cols.mass[top]! <= EMPTY_MASS) cols.count[cell]!--;
  }
  return { mass: mass - remaining, thickness };
}

/** Mass (kg/m²) in the contiguous run of top layers that `canErode` accepts. */
export function erodibleMass(
  cols: SnowColumns,
  cell: number,
  canErode: (grain: Grain, depositedAt: number) => boolean,
): number {
  const base = cell * cols.maxLayers;
  let sum = 0;
  for (let k = cols.count[cell]! - 1; k >= 0; k--) {
    if (!canErode(cols.grain[base + k]! as Grain, cols.depositedAt[base + k]!)) break;
    sum += cols.mass[base + k]!;
  }
  return sum;
}

/** The top layer's index into the layer arrays, or −1 for bare ground. */
export function topLayer(cols: SnowColumns, cell: number): number {
  const n = cols.count[cell]!;
  return n === 0 ? -1 : cell * cols.maxLayers + n - 1;
}

export function columnDepth(cols: SnowColumns, cell: number): number {
  const base = cell * cols.maxLayers;
  let d = 0;
  for (let k = 0; k < cols.count[cell]!; k++) d += cols.mass[base + k]! / cols.density[base + k]!;
  return d;
}

export function columnMass(cols: SnowColumns, cell: number): number {
  const base = cell * cols.maxLayers;
  let m = 0;
  for (let k = 0; k < cols.count[cell]!; k++) m += cols.mass[base + k]!;
  return m;
}

/** Liquid water in a column (kg/m²). */
export function columnWater(cols: SnowColumns, cell: number): number {
  const base = cell * cols.maxLayers;
  let w = 0;
  for (let k = 0; k < cols.count[cell]!; k++) w += cols.water[base + k]!;
  return w;
}

export function depthField(cols: SnowColumns): ScalarField {
  const out = createField(cols.spec);
  for (let i = 0; i < out.data.length; i++) out.data[i] = columnDepth(cols, i);
  return out;
}

/** Snow water equivalent including liquid water held in the snow (kg/m²). */
export function sweField(cols: SnowColumns): ScalarField {
  const out = createField(cols.spec);
  for (let i = 0; i < out.data.length; i++)
    out.data[i] = columnMass(cols, i) + columnWater(cols, i);
  return out;
}

/** Total snow mass over the grid (kg). */
export function totalMass(cols: SnowColumns): number {
  let m = 0;
  for (let i = 0; i < cols.count.length; i++) m += columnMass(cols, i);
  return m * cols.spec.cellSize * cols.spec.cellSize;
}
